import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { ChartCard } from "@repo/ui/Cards/chartCard";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { useState, type FC } from "react";
import { useCreateFunnelMutation } from "../../../api/entities/funnel.js";
import { useTeammateAuthorization } from "../../../helpers/teammateAuthorizationContext.js";
import { FunnelSettingsFields } from "./funnelSettingsFields.js";

/** What the funnel is about: a voice agent, or a playbook's gradings. */
export type FunnelCreateSubject =
  | { teammateAgentId: TeammateId }
  | { playbookId: PlaybookId };

export interface FunnelCreateFormProps {
  subject: FunnelCreateSubject;
}

/** Shown while the subject has no funnel: creating one starts generating it. */
export const FunnelCreateForm: FC<FunnelCreateFormProps> = (props) => {
  const { subject } = props;
  const isPlaybook = "playbookId" in subject;

  const [sellingStrategy, setSellingStrategy] = useState("");
  // A playbook funnel compares its reps with the same playbook's top reps.
  const [playbookIds, setPlaybookIds] = useState<readonly PlaybookId[]>(
    isPlaybook ? [subject.playbookId] : [],
  );
  const [createFunnel, { isLoading: isCreating }] = useCreateFunnelMutation();
  const canEdit = useTeammateAuthorization().can(
    PermissionActions.UPDATE,
    PermissionSubjects.TEAMMATES,
  );

  const handleCreate = async () => {
    try {
      await createFunnel({
        voiceAgentTeammateIds: isPlaybook ? [] : [subject.teammateAgentId],
        playbookIds: [...playbookIds],
        sellingStrategy,
      }).unwrap();
    } catch (error) {
      console.error("Failed to create funnel:", error);
      showToast({
        variant: ToastVariant.Error,
        title: "Couldn't create the funnel",
        description: "Please try again.",
      });
    }
  };

  return (
    <ChartCard
      title="Create a funnel"
      subtitle={
        isPlaybook
          ? "A funnel shows the stages this playbook's interactions move through, and the moments of friction in each stage, such as callers' questions and objections, with how the top reps handle them. Generating the stages and moments takes a few minutes."
          : "A funnel shows the stages this agent's interactions move through, and the moments of friction in each stage, such as callers' questions and objections or where the agent breaks down. Generating the stages and moments takes a few minutes."
      }
    >
      <div className="flex flex-col gap-4">
        <FunnelSettingsFields
          sellingStrategy={sellingStrategy}
          onSellingStrategyChange={setSellingStrategy}
          playbookIds={playbookIds}
          onPlaybookIdsChange={setPlaybookIds}
          isPlaybookSubject={isPlaybook}
          isDisabled={!canEdit || isCreating}
        />
        <div className="flex justify-end">
          <Button
            variant={ButtonVariant.Primary}
            onClick={() => void handleCreate()}
            isLoading={isCreating}
            isDisabled={!canEdit || sellingStrategy.trim() === ""}
          >
            Create funnel
          </Button>
        </div>
      </div>
    </ChartCard>
  );
};
